import ProgressBar from 'react-bootstrap/lib/ProgressBar';
import Button from 'react-bootstrap/lib/Button';
import Tooltip from 'react-bootstrap/lib/Tooltip';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import PropTypes from 'prop-types';
import FileUploadLabel from './FileUploadLabel';
import ConfirmationModal from '../ConfirmationModal';
import { SplitFileName } from '../../../src/util/StringUtil';
import React, { Component } from 'react';
import Icon from '../Icon';
import i18n from '../../../src/i18n/translate';
import { pickDataAttributes } from '../../Util';

export default class FileUpload extends Component {
  static propTypes = {
    accept: PropTypes.array,
    actions: PropTypes.shape({
      success: PropTypes.func,
      failure: PropTypes.func,
      delete: PropTypes.func
    }),
    button: PropTypes.shape({
      className: PropTypes.string,
      dataAttributes: PropTypes.object,
      text: PropTypes.string
    }),
    className: PropTypes.string,
    deleteButton: PropTypes.bool,
    deleteButtonDataAttributes: PropTypes.object,
    description: PropTypes.shape({
      textArray: PropTypes.array,
      className: PropTypes.string
    }),
    embedPreviewUrl: PropTypes.string,
    files: PropTypes.array,
    header: PropTypes.oneOf([PropTypes.shape({
      text: PropTypes.string,
      className: PropTypes.string
    }), PropTypes.boolean]),
    icon: PropTypes.shape({
      className: PropTypes.string,
      iconSize: PropTypes.string,
      fileClassName: PropTypes.string,
      noFileClassName: PropTypes.string
    }),
    instructionalText: PropTypes.string,
    label: PropTypes.shape({
      text: PropTypes.string,
      className: PropTypes.string,
      closable: PropTypes.bool
    }),
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onUpload: PropTypes.func,
    parameters: PropTypes.object,
    required: PropTypes.bool,
    saveButton: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        className: PropTypes.string
      })
    ]),
    templateBtn: PropTypes.shape({
      href: PropTypes.string.isRequired,
      buttonLabel: PropTypes.string.isRequired
    }),
    tooltip: PropTypes.string,
    uploader: PropTypes.func,
    url: PropTypes.string
  };

  static defaultProps = {
    label: {},
    description: { textArray: [], className: 'file-upload-description' },
    icon: { markup: '', className: 'file-upload-icon', fileClassName: '', noFileClassName: '' },
    header: { className: 'file-upload-header' },
    button: { className: 'btn btn-primary' },
    multiple: false,
    accept: ['*'],
    files: []
  };

  constructor(props) {
    super(props);
    this.state = {
      files: props.files,
      removed: [],
      showDeleteModal: false
    };
  }

  getPreviewSrc = file => {
    if (this.props.multiple) return null;

    return file.type?.includes('image') && window.URL.createObjectURL(file);
  }

  addFiles = (e) => {
    this.setState({
      removed: [],
      files: Array.from(e.currentTarget.files),
      previewSrc: this.getPreviewSrc(e.currentTarget.files[0])
    });

    if (this.props.onUpload) {
      const files = this.activeFiles();
      this.props.onUpload(files)
    } else if (!this.props.saveButton) {
      this.save();
    }
  }

  removeFile = (e, name) => {
    if (!this.state.removed.includes(name)) {
      this.state.removed.push(name);
    }
    if (this.fileInput !== undefined) {
      this.setState({ files: this.activeFiles() });
    }
  }

  activeFiles = () => {
    return Array.from(this.fileInput.files).filter((file) => {
      return !this.state.removed.includes(file.name);
    });
  }

  openFileDialogue = (e) => {
    e.preventDefault();
    if (this.fileInput !== undefined) {
      this.fileInput.click();
      this.fileInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  validateFiles = (files) => {
    var MIMEtypes = this.props.accept.map(function (type) {
      return new RegExp(type.trim().replace('*', '[^\\/,]+'));
    });

    return files.every(function (file) {
      const fileType = file.type || SplitFileName(file.name).extension;
      return MIMEtypes.some(function (MIMEtype) {
        return MIMEtype.test(fileType);
      });
    });
  }

  save = async () => {
    const files = this.activeFiles();
    if (!this.validateFiles(files)) {
      this.props.actions.failure({ "message": i18n.t('Cannot save file: incorrect file type') });
      return;
    }
    let response = null;
    try {
      this.fileInput.setCustomValidity('File is uploading!');
      this.onUploadProgress(0, 100, false);
      response = await this.props.uploader(files, this.props.url, this.props.parameters, this.onUploadProgress);
      if (response && this.props.actions && this.props.actions.success) {
        this.onUploadProgress(100, 100, true);
        this.props.actions.success(response);
      }
    } catch (error) {
      this.fileInput.setCustomValidity('File upload failed!');
      if (this.props.actions && this.props.actions.failure) {
        this.props.actions.failure(error.errors ? { "message": error.errors.map(error => { return error.detail; }).join(',') } : error);
      }
    }
  }

  onUploadProgress = (current, total, complete) => {
    if (total && total > 0) {
      if (this.fileInput && complete) {
        this.fileInput.setCustomValidity('');
      }
      this.setState({
        progress: {
          current,
          total,
          percentage: (current / total) * 100,
          complete
        }
      });
    }
  }

  deleteFile = () => {
    this.setState({ showDeleteModal: true });
  }

  confirmDelete = () => {
    let { name } = this.props
    if (typeof this.props.actions.delete === 'function' && name) {
      this.props.actions.delete(name);

      if (this.state.previewSrc) {
        window.URL.revokeObjectURL(this.state.previewSrc)
      }
    }
    this.cancelDelete();
  }

  cancelDelete = () => {
    this.setState({ showDeleteModal: false });
  }

  render() {
    const previewUrl = this.props.embedPreviewUrl || this.state.previewSrc;
    const dataAttributes = pickDataAttributes(this.props);

    return (
      <div className={`${this.props.className} file-upload`} {...dataAttributes}>
        {this.props.header &&
          <h4 className={this.props.header.className}>{this.props.header.text || i18n.t('File Upload')} {this.props.tooltip &&
            <span className="tooltip-container">
              <OverlayTrigger placement="top" overlay={(<Tooltip id="tooltip-">{this.props.tooltip}</Tooltip>)}>
                <span><Icon iconType="help-outline" /></span>
              </OverlayTrigger>
            </span>}
          </h4>
        }
        {this.props.instructionalText && <div className="instructional-text">{this.props.instructionalText}</div>}
        {this.props.description && <p className={this.props.description.className}>
          {
            this.props.description.textArray.map((desc) => {
              return (<span key={desc}>{desc} <br /></span>)
            })
          }
        </p>}
        <input
          required={this.props.required}
          type="file"
          ref={i => { this.fileInput = i }}
          onChange={this.addFiles}
          style={{ display: 'none' }}
          multiple={this.props.multiple}
          accept={this.props.accept.join(',')} />
        <div className="file-upload-icon-container">
          <div className="file-upload-icon">
            <div className={this.state.files.length > 0 ? this.props.icon.fileClassName : this.props.icon.noFileClassName}><Icon iconType={this.props.icon.className} size={this.props.icon.iconSize} /></div>
          </div>
          <div className="file-upload-label">
            {this.state.files.length > 0 ?
              this.state.files.map((f) => {
                return <FileUploadLabel onClick={this.openFileDialogue} text={f.name} key={f.name} removeItem={this.removeFile} closable={this.props.label.closable} />
              }) : <FileUploadLabel onClick={this.openFileDialogue} text={this.props.label.text || i18n.t('No File')} removeItem={this.removeFile} closable={this.props.label.closable} />
            }
          </div>
          {this.state.progress ? <ProgressBar now={this.state.progress.percentage} /> : null}
        </div>
        {previewUrl && <div>
          <img src={previewUrl} width="230px" />
        </div>}
        <div className="btn-group">
          <span>
            <Button
              {...this.props.button?.dataAttributes}
              className={this.props.button.className}
              data-action={this.props.button?.dataAttributes?.["data-action"] || "choose-a-file"}
              onClick={this.openFileDialogue}
            >
              <Icon iconType="cloud-upload" />
              {this.props.button.text || i18n.t('Browse')}
            </Button>
          </span>
          {this.props.deleteButton &&
            <span className="file-upload-delete">
              <a
                {...this.props.deleteButtonDataAttributes}
                onClick={this.deleteFile}
              >
                <Icon iconType="delete" />{i18n.t('Delete')}
              </a>
            </span>}
          {this.props.templateBtn && Object.keys(this.props.templateBtn).length && (
            <span>
              <Button
                className="btn-secondary"
                download={true}
                href={this.props.templateBtn.href}
              >
                <Icon iconType="download" />
                {this.props.templateBtn.buttonLabel}
              </Button>
            </span>
          )}
        </div>
        {this.props.saveButton && <Button onClick={this.save} className={this.props.saveButton.className || 'btn btn-success'}>{i18n.t("Save")}</Button>}

        <ConfirmationModal
          title={i18n.t('Delete this file?')}
          show={this.state.showDeleteModal}
          onCancel={this.cancelDelete}
          onConfirm={this.confirmDelete}>
          {i18n.t('Are you sure you want to delete this file?')}
        </ConfirmationModal>
      </div>
    );
  }

  static types = {
    audio: ['audio/*'],
    video: ['video/*'],
    images: ['images/*'],
    text: ['text/plain'],
    html: ['text/html'],
    pdf: ['application/pdf'],
    csv: ['text/csv']
  }
}
